/**
 * ExamApplicationPage
 * Main page for managing exam applications across organizational levels
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  Stack,
  ListItemText,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { PageShell } from '../../components';
import { InlineFilterBar, InlineSelect } from '../../components/common';
import type { ActiveChip } from '../../hooks/useFilterState';
import { DualTransferList } from '../../components/exam-applications';
import { ExamApplicationModal } from '../../components/exam-applications';
import { ExamExpirationBadge } from '../../components/exam-applications';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { occupationService } from '../../services/occupationService';
import { employeeService } from '../../services/employeeService';
import {
  ApplicationLevel,
  ApplicationLevelLabels,
  ApplicationData,
  ExamWithApplication,
  AvailableExam,
  ExpirationStatus,
  getExpirationStatus,
  isExamWithApplication,
} from '../../types/examApplication';
import { useExamApplications } from './hooks/useExamApplications';

// ============================================================================
// TYPES
// ============================================================================

interface SelectOption {
  id: number;
  name: string;
  parentId?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function TabPanel({ children, value, index }: TabPanelProps): React.ReactElement | null {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const LEVEL_OPTIONS = Object.values(ApplicationLevel).map((level) => ({
  value: level,
  label: ApplicationLevelLabels[level],
}));

// ============================================================================
// COMPONENT
// ============================================================================

export const ExamApplicationPage: React.FC = () => {
  // Exam application state from hook
  const {
    applied,
    available,
    alerts,
    loading,
    loadingAlerts,
    error,
    alertsError,
    loadExams,
    loadAlerts,
    applyMultipleExams,
    removeMultipleExams,
    updateApplication,
    clearError,
    clearAlertsError,
  } = useExamApplications();

  // Local UI state
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLevel, setSelectedLevel] = useState<ApplicationLevel>(
    ApplicationLevel.COMPANY
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [selectedOccupationId, setSelectedOccupationId] = useState<number | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [highlightField, setHighlightField] = useState<string | null>(null);

  // Options for selectors
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [departments, setDepartments] = useState<SelectOption[]>([]);
  const [occupations, setOccupations] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'apply' | 'edit'>('apply');
  const [selectedExam, setSelectedExam] = useState<
    AvailableExam | ExamWithApplication | null
  >(null);
  const [pendingApplyExams, setPendingApplyExams] = useState<AvailableExam[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================================================
  // LOAD OPTIONS
  // ============================================================================

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingOptions(true);
        const response = await companyService.getCompanies({ limit: 1000 });
        setCompanies(
          response.data.map((c) => ({
            id: Number(c.id),
            name: c.tradeName || c.legalName,
          }))
        );
      } catch (err) {
        console.error('Error loading companies:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadCompanies();
  }, []);

  // Load departments when company changes
  useEffect(() => {
    if (!selectedCompanyId) {
      setDepartments([]);
      return;
    }

    const loadDepartments = async () => {
      try {
        const response = await departmentService.getDepartments({
          companyId: String(selectedCompanyId),
          limit: 1000,
        });
        setDepartments(
          response.data.map((d) => ({
            id: Number(d.id),
            name: d.name,
            parentId: Number(d.companyId),
          }))
        );
      } catch (err) {
        console.error('Error loading departments:', err);
      }
    };
    loadDepartments();
  }, [selectedCompanyId]);

  // Load occupations when department changes
  useEffect(() => {
    if (!selectedDepartmentId) {
      setOccupations([]);
      return;
    }

    const loadOccupations = async () => {
      try {
        const response = await occupationService.getOccupations({
          limit: 1000,
          filters: { departmentId: selectedDepartmentId },
        });
        setOccupations(
          response.data.map((o) => ({
            id: o.id,
            name: o.name,
            parentId: o.departmentId,
          }))
        );
      } catch (err) {
        console.error('Error loading occupations:', err);
      }
    };
    loadOccupations();
  }, [selectedDepartmentId]);

  // Load employees when occupation changes
  useEffect(() => {
    if (!selectedOccupationId) {
      setEmployees([]);
      return;
    }

    const loadEmployees = async () => {
      try {
        const response = await employeeService.getEmployees({
          limit: 1000,
          filters: { occupationId: selectedOccupationId },
        });
        setEmployees(
          response.data.map((e) => ({
            id: e.id,
            name: e.name,
            parentId: e.occupationId,
          }))
        );
      } catch (err) {
        console.error('Error loading employees:', err);
      }
    };
    loadEmployees();
  }, [selectedOccupationId]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const currentTargetId = useMemo((): number | null => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return selectedCompanyId;
      case ApplicationLevel.DEPARTMENT:
        return selectedDepartmentId;
      case ApplicationLevel.OCCUPATION:
        return selectedOccupationId;
      case ApplicationLevel.EMPLOYEE:
        return selectedEmployeeId;
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
  ]);

  const getEmptyStateMessage = (): string => {
    if (!selectedCompanyId) return 'Selecione uma empresa para gerenciar os exames.';
    const missing: string[] = [];
    if (selectedLevel !== ApplicationLevel.COMPANY && !selectedDepartmentId) missing.push('um setor');
    if ((selectedLevel === ApplicationLevel.OCCUPATION || selectedLevel === ApplicationLevel.EMPLOYEE) && !selectedOccupationId) missing.push('um cargo');
    if (selectedLevel === ApplicationLevel.EMPLOYEE && !selectedEmployeeId) missing.push('um funcionário');
    if (missing.length === 0) return 'Selecione um destino para gerenciar os exames.';
    return `Selecione ${missing.join(', ')} para gerenciar os exames.`;
  };

  const alertsCounts = useMemo(() => {
    if (!alerts) return { expiring: 0, expired: 0 };
    return {
      expiring: alerts.expiring.length,
      expired: alerts.expired.length,
    };
  }, [alerts]);

  // ============================================================================
  // LOAD EXAMS WHEN TARGET CHANGES
  // ============================================================================

  useEffect(() => {
    if (currentTargetId) {
      loadExams(selectedLevel, currentTargetId);
    }
  }, [selectedLevel, currentTargetId, loadExams]);

  // Load alerts when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadAlerts(selectedCompanyId, 30);
    }
  }, [selectedCompanyId, loadAlerts]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const pulseAnimation = {
    '@keyframes borderPulse': {
      '0%': { borderColor: 'error.main', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
      '50%': { borderColor: 'error.main', boxShadow: '0 0 0 4px rgba(211, 47, 47, 0.1)' },
      '100%': { borderColor: 'divider', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      animation: 'borderPulse 1.5s ease-in-out infinite',
    },
  };

  const triggerHighlight = (field: string) => {
    setHighlightField(field);
  };

  const handleLevelChange = (level: ApplicationLevel) => {
    setSelectedLevel(level);
    // Reset dependent selections and highlight the next field to fill
    if (level === ApplicationLevel.COMPANY) {
      setSelectedDepartmentId(null);
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
    } else if (level === ApplicationLevel.DEPARTMENT) {
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
      triggerHighlight('department');
    } else if (level === ApplicationLevel.OCCUPATION) {
      setSelectedEmployeeId(null);
      if (!selectedDepartmentId) {
        triggerHighlight('department');
      } else {
        triggerHighlight('occupation');
      }
    } else if (level === ApplicationLevel.EMPLOYEE) {
      if (!selectedDepartmentId) {
        triggerHighlight('department');
      } else if (!selectedOccupationId) {
        triggerHighlight('occupation');
      } else {
        triggerHighlight('employee');
      }
    }
  };

  const handleApply = useCallback(
    async (items: (AvailableExam | ExamWithApplication)[]) => {
      const availableItems = items.filter(
        (item): item is AvailableExam => !('applicationLevel' in item)
      );

      if (availableItems.length === 0) return;

      // Store pending exams and open modal for first one
      setPendingApplyExams(availableItems);
      setSelectedExam(availableItems[0]);
      setModalMode('apply');
      setModalOpen(true);
    },
    []
  );

  const handleRemove = useCallback(
    async (items: (AvailableExam | ExamWithApplication)[]) => {
      if (!currentTargetId) return;

      const appliedItems = items.filter(
        (item): item is ExamWithApplication => 'applicationLevel' in item
      );

      if (appliedItems.length === 0) return;

      const examIds = appliedItems.map((item) => item.examId);
      const success = await removeMultipleExams(selectedLevel, currentTargetId, examIds);

      if (success) {
        setSnackbar({
          open: true,
          message: `${examIds.length} exame(s) removido(s) com sucesso`,
          severity: 'success',
        });
      }
    },
    [currentTargetId, selectedLevel, removeMultipleExams]
  );

  const handleModalConfirm = useCallback(
    async (data: ApplicationData) => {
      if (!currentTargetId || pendingApplyExams.length === 0) return;

      const examIds = pendingApplyExams.map((exam) => exam.id);
      const success = await applyMultipleExams(
        selectedLevel,
        currentTargetId,
        examIds,
        data
      );

      if (success) {
        setSnackbar({
          open: true,
          message: `${examIds.length} exame(s) aplicado(s) com sucesso`,
          severity: 'success',
        });
        setModalOpen(false);
        setPendingApplyExams([]);
        setSelectedExam(null);
      }
    },
    [currentTargetId, selectedLevel, pendingApplyExams, applyMultipleExams]
  );

  const handleEditConfirm = useCallback(
    async (data: ApplicationData) => {
      if (!currentTargetId || !selectedExam || !('examId' in selectedExam)) return;

      const success = await updateApplication(
        selectedLevel,
        currentTargetId,
        (selectedExam as ExamWithApplication).examId,
        data
      );

      if (success) {
        setSnackbar({
          open: true,
          message: 'Aplicação atualizada com sucesso',
          severity: 'success',
        });
        setModalOpen(false);
        setSelectedExam(null);
      }
    },
    [currentTargetId, selectedLevel, selectedExam, updateApplication]
  );

  // Engrenagem: abre o modal de detalhes da aplicação (periodicidade, datas, etc.)
  const handleItemConfigure = useCallback(
    (item: AvailableExam | ExamWithApplication) => {
      if (!isExamWithApplication(item)) return;

      setSelectedExam(item);
      setModalMode('edit');
      setModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setPendingApplyExams([]);
    setSelectedExam(null);
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Options for the inline selects (ids as strings — InlineSelect works on string values)
  const companyOptions = companies.map((c) => ({ value: String(c.id), label: c.name }));
  const departmentOptions = departments.map((d) => ({ value: String(d.id), label: d.name }));
  const occupationOptions = occupations.map((o) => ({ value: String(o.id), label: o.name }));
  const employeeOptions = employees.map((e) => ({ value: String(e.id), label: e.name }));

  // Active chips mirror the selection cascade: removing one clears it and everything below it.
  const filterChips: ActiveChip[] = [];
  if (selectedCompanyId) {
    filterChips.push({
      key: 'company',
      label: 'Empresa',
      value: companies.find((c) => c.id === selectedCompanyId)?.name ?? String(selectedCompanyId),
      onDelete: () => {
        setSelectedCompanyId(null);
        setSelectedDepartmentId(null);
        setSelectedOccupationId(null);
        setSelectedEmployeeId(null);
        setHighlightField(null);
      },
    });
  }
  if (selectedDepartmentId) {
    filterChips.push({
      key: 'department',
      label: 'Setor',
      value: departments.find((d) => d.id === selectedDepartmentId)?.name ?? String(selectedDepartmentId),
      onDelete: () => {
        setSelectedDepartmentId(null);
        setSelectedOccupationId(null);
        setSelectedEmployeeId(null);
        setHighlightField(null);
      },
    });
  }
  if (selectedOccupationId) {
    filterChips.push({
      key: 'occupation',
      label: 'Cargo',
      value: occupations.find((o) => o.id === selectedOccupationId)?.name ?? String(selectedOccupationId),
      onDelete: () => {
        setSelectedOccupationId(null);
        setSelectedEmployeeId(null);
        setHighlightField(null);
      },
    });
  }
  if (selectedEmployeeId) {
    filterChips.push({
      key: 'employee',
      label: 'Funcionário',
      value: employees.find((e) => e.id === selectedEmployeeId)?.name ?? String(selectedEmployeeId),
      onDelete: () => {
        setSelectedEmployeeId(null);
        setHighlightField(null);
      },
    });
  }

  const handleClearAllFilters = () => {
    setSelectedLevel(ApplicationLevel.COMPANY);
    setSelectedCompanyId(null);
    setSelectedDepartmentId(null);
    setSelectedOccupationId(null);
    setSelectedEmployeeId(null);
    setHighlightField(null);
  };

  const renderLevelSelector = () => (
    <Box sx={{ mb: 3 }}>
      <InlineFilterBar
        resultCount={currentTargetId ? applied.length + available.length : undefined}
        resultNoun="exames"
        chips={filterChips}
        onClearAll={handleClearAllFilters}
        loading={loading || loadingOptions}
      >
        <InlineSelect
          label="Nível"
          value={selectedLevel}
          options={LEVEL_OPTIONS}
          onChange={(v) => handleLevelChange(v as ApplicationLevel)}
          minWidth={170}
        />
        <InlineSelect
          label="Empresa"
          value={selectedCompanyId ? String(selectedCompanyId) : ''}
          options={companyOptions}
          onChange={(v) => {
            setSelectedCompanyId(v ? Number(v) : null);
            setSelectedDepartmentId(null);
            setSelectedOccupationId(null);
            setSelectedEmployeeId(null);
          }}
          disabled={loadingOptions}
          minWidth={200}
        />

        {/* Department Selector (shown for department, occupation, employee) */}
        {selectedLevel !== ApplicationLevel.COMPANY && (
          <Box sx={highlightField === 'department' ? pulseAnimation : undefined}>
            <InlineSelect
              label="Setor"
              value={selectedDepartmentId ? String(selectedDepartmentId) : ''}
              options={departmentOptions}
              onChange={(v) => {
                setSelectedDepartmentId(v ? Number(v) : null);
                setSelectedOccupationId(null);
                setSelectedEmployeeId(null);
                if (selectedLevel === ApplicationLevel.OCCUPATION || selectedLevel === ApplicationLevel.EMPLOYEE) {
                  triggerHighlight('occupation');
                } else {
                  setHighlightField(null);
                }
              }}
              disabled={!selectedCompanyId || departments.length === 0}
              minWidth={190}
            />
          </Box>
        )}

        {/* Occupation Selector (shown for occupation, employee) */}
        {(selectedLevel === ApplicationLevel.OCCUPATION ||
          selectedLevel === ApplicationLevel.EMPLOYEE) && (
          <Box sx={highlightField === 'occupation' ? pulseAnimation : undefined}>
            <InlineSelect
              label="Cargo"
              value={selectedOccupationId ? String(selectedOccupationId) : ''}
              options={occupationOptions}
              onChange={(v) => {
                setSelectedOccupationId(v ? Number(v) : null);
                setSelectedEmployeeId(null);
                if (selectedLevel === ApplicationLevel.EMPLOYEE) {
                  triggerHighlight('employee');
                } else {
                  setHighlightField(null);
                }
              }}
              disabled={!selectedDepartmentId || occupations.length === 0}
              minWidth={190}
            />
          </Box>
        )}

        {/* Employee Selector (shown for employee only) */}
        {selectedLevel === ApplicationLevel.EMPLOYEE && (
          <Box sx={highlightField === 'employee' ? pulseAnimation : undefined}>
            <InlineSelect
              label="Funcionário"
              value={selectedEmployeeId ? String(selectedEmployeeId) : ''}
              options={employeeOptions}
              onChange={(v) => {
                setSelectedEmployeeId(v ? Number(v) : null);
                setHighlightField(null);
              }}
              disabled={!selectedOccupationId || employees.length === 0}
              minWidth={190}
            />
          </Box>
        )}
      </InlineFilterBar>
    </Box>
  );

  const renderExamTransferList = () => (
    <Card>
      <CardHeader
        title="Gerenciar Exames"
        subheader={
          currentTargetId
            ? `Aplicar ou remover exames de ${ApplicationLevelLabels[selectedLevel].toLowerCase()}`
            : 'Selecione um destino para gerenciar exames'
        }
      />
      <CardContent>
        {!currentTargetId ? (
          <Alert severity="info">{getEmptyStateMessage()}</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DualTransferList
            available={available}
            applied={applied}
            onApply={handleApply}
            onRemove={handleRemove}
            getItemKey={(item) => ('examId' in item ? item.examId : item.id) as string | number}
            getItemLabel={(item) => item.examName}
            getItemSecondary={(item) =>
              'esocialCode' in item && item.esocialCode ? item.esocialCode : undefined
            }
            searchFields={['examName', 'esocialCode']}
            availableTitle="Exames Disponíveis"
            appliedTitle="Exames Aplicados"
            loading={loading}
            renderItem={(item, isApplied) => (
              <ListItemText
                primary={item.examName}
                secondary={
                  isApplied && 'applicationLevel' in item ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <ExamExpirationBadge
                        status={getExpirationStatus(item as ExamWithApplication)}
                        daysUntilExpiration={(item as ExamWithApplication).daysUntilExpiration}
                        size="small"
                      />
                      {(item as ExamWithApplication).periodicityMonths && (
                        <Typography variant="caption" color="text.secondary">
                          {(item as ExamWithApplication).periodicityMonths} meses
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    item.esocialCode ?? undefined
                  )
                }
              />
            )}
            onItemConfigure={handleItemConfigure}
          />
        )}
      </CardContent>
    </Card>
  );

  const renderAlertsTab = () => (
    <Card>
      <CardHeader
        title="Alertas de Vencimento"
        subheader="Exames próximos do vencimento ou já vencidos"
      />
      <CardContent>
        {!selectedCompanyId ? (
          <Alert severity="info">
            Selecione uma empresa para visualizar os alertas de vencimento.
          </Alert>
        ) : loadingAlerts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : alertsError ? (
          <Alert severity="error" onClose={clearAlertsError}>
            {alertsError}
          </Alert>
        ) : !alerts || (alerts.expired.length === 0 && alerts.expiring.length === 0) ? (
          <Alert severity="success">
            Nenhum exame vencido ou próximo do vencimento.
          </Alert>
        ) : (
          <Box>
            {alerts.expired.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                  Exames Vencidos ({alerts.expired.length})
                </Typography>
                {alerts.expired.map((alert) => (
                  <Card
                    key={`expired-${alert.applicationId}`}
                    variant="outlined"
                    sx={{ mb: 1, borderColor: 'error.main' }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="subtitle2">{alert.examName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                          </Typography>
                        </Box>
                        <ExamExpirationBadge
                          status={ExpirationStatus.EXPIRED}
                          daysUntilExpiration={-alert.daysOverdue}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {alerts.expiring.length > 0 && (
              <Box>
                <Typography variant="h6" color="warning.main" sx={{ mb: 2 }}>
                  Exames Vencendo ({alerts.expiring.length})
                </Typography>
                {alerts.expiring.map((alert) => (
                  <Card
                    key={`expiring-${alert.applicationId}`}
                    variant="outlined"
                    sx={{ mb: 1, borderColor: 'warning.main' }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="subtitle2">{alert.examName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                          </Typography>
                        </Box>
                        <ExamExpirationBadge
                          status={ExpirationStatus.EXPIRING_SOON}
                          daysUntilExpiration={alert.daysUntilExpiration}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Medicina', path: '/medicina' },
        { label: 'Aplicação de Exames' },
      ]}
      title="Aplicação de Exames Ocupacionais"
      subtitle="Gerencie a aplicação de exames em empresas, setores, cargos e funcionários"
    >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {/* Level Selector */}
        {renderLevelSelector()}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Gerenciar Exames" />
            <Tab
              label={
                <Badge
                  badgeContent={alertsCounts.expired + alertsCounts.expiring}
                  color="error"
                  max={99}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon fontSize="small" />
                    Alertas
                  </Box>
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          {renderExamTransferList()}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {renderAlertsTab()}
        </TabPanel>

        {/* Modal */}
        <ExamApplicationModal
          open={modalOpen}
          onClose={handleCloseModal}
          onConfirm={modalMode === 'apply' ? handleModalConfirm : handleEditConfirm}
          exam={selectedExam}
          mode={modalMode}
          loading={loading}
          error={error}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </PageShell>
  );
};

export default ExamApplicationPage;
